import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { GroupService } from "src/app/_services/group.service";
import {
  AddGroup,
  AddGroupError,
  AddGroupSuccess,
  DeleteGroups,
  DeleteGroupsError,
  DeleteGroupsSuccess,
  GetCurrentPageNumber,
  GetCurrentPageNumberError,
  GetCurrentPageNumberSuccess,
  GetGroupByOrgOptionalCentre,
  GetGroupByOrgOptionalCentreError,
  GetGroupByOrgOptionalCentreSuccess,
  GetGroupByOrgOptionalCentreTutorFlow,
  GetGroupByOrgOptionalCentreTutorFlowError,
  GetGroupByOrgOptionalCentreTutorFlowSuccess,
  GetGroupTable,
  GetGroupTableError,
  GetGroupTableLength,
  GetGroupTableLengthError,
  GetGroupTableLengthSuccess,
  GetGroupTableSuccess,
  GetOrgCentreGroupList,
  GetOrgCentreGroupListError,
  GetOrgCentreGroupListSuccess,
  ProcessGetLearnerIdsToAssign,
  ProcessGetLearnerIdsToAssignError,
  ProcessGetLearnerIdsToAssignSuccess,
  UpdateGroupTable,
  UpdateGroupTableError,
} from "./group.actions";
import {
  catchError,
  concatMap,
  map,
  mergeMap,
  switchMap,
  toArray,
  withLatestFrom,
} from "rxjs/operators";
import { Group, GroupList } from "src/app/_models/Group";
import { of } from "rxjs";
import { HttpErrorResponse } from "@angular/common/http";
import { Store } from "@ngrx/store";
import { selectGroupListInitial } from "./group.selectors";
import { splitArrayChunks } from "src/app/shared/split-array-chunks";
import { DeleteGroupDto } from "src/app/_models/DeleteGroupDto";

@Injectable()
export class GroupEffects {
  constructor(
    private action$: Actions,
    private groupService: GroupService,
    private store: Store
  ) {}

  public addGroupEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(AddGroup),
      switchMap((payload) =>
        this.groupService.addGroup$(payload.addGroupDto).pipe(
          map((result: Group) => {
            return AddGroupSuccess({ result: result });
          }),
          catchError((error: HttpErrorResponse) => {
            return of(AddGroupError({ crudError: error }));
          })
        )
      )
    )
  );

  public getGroupTableEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(GetGroupTable),
      withLatestFrom(this.store.select(selectGroupListInitial)),
      mergeMap(([action, isInitial]: [{ pageIndex: number }, boolean]) => {
        return this.groupService
          .getGroupTableRecord$(action.pageIndex, isInitial)
          .pipe(
            map((result: GroupList[]) => {
              this.store.dispatch(GetGroupTableLength());
              return GetGroupTableSuccess({ result: result });
            }),
            catchError((error: any) => {
              return of(GetGroupTableError({ error: error }));
            })
          );
      })
    )
  );

  public getGroupTableLengthEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(GetGroupTableLength),
      switchMap(() =>
        this.groupService.getGroupTableLength$().pipe(
          map((result: number) => {
            return GetGroupTableLengthSuccess({ result: result });
          }),
          catchError((error: any) => {
            return of(GetGroupTableLengthError({ error: error }));
          })
        )
      )
    )
  );

  public updateGroupTableListEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(UpdateGroupTable),
      switchMap((payload) =>
        this.groupService.updateGroupTableRecords$(payload.request).pipe(
          map(() => {
            return GetGroupTable({ pageIndex: payload.request.pageIndex });
          }),
          catchError((error: any) => {
            return of(UpdateGroupTableError({ error: error }));
          })
        )
      )
    )
  );

  public getGroupListByOrgAndCentreEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(GetOrgCentreGroupList),
      switchMap((payload) =>
        this.groupService.getGroupListByOrgAndCentre$(payload.collections).pipe(
          map((result: Group[]) => {
            return GetOrgCentreGroupListSuccess({ result: result });
          }),
          catchError((error: HttpErrorResponse) => {
            return of(GetOrgCentreGroupListError({ error: error }));
          })
        )
      )
    )
  );

  public deleteGroupsEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(DeleteGroups),
      switchMap((data) =>
        this.groupService.validateDeleteGroups$(data.params).pipe(
          map((result) => {
            if (result) {
              var splittedGroupIds = splitArrayChunks(data.params, 10);
              var request: DeleteGroupDto[] = [];

              splittedGroupIds.forEach((groupIds) => {
                request.push({ groupIds: groupIds });
              });

              return request;
            }
          }),
          mergeMap((data) => {
            return of(...data).pipe(
              concatMap((request) =>
                this.groupService.deleteGroups$(request.groupIds)
              ),
              toArray(),
              map((result: boolean[]) => {
                if (result.every((isSuccess) => isSuccess)) {
                  return DeleteGroupsSuccess({ isDeletedSuccessFully: true });
                } else {
                  return DeleteGroupsSuccess({ isDeletedSuccessFully: false });
                }
              })
            );
          }),
          catchError((error: any) => {
            return of(DeleteGroupsError({ error: error }));
          })
        )
      ),
    )
  );

  public getCurrentPageNumberEffect$ = createEffect(() =>
    this.action$.pipe(
        ofType(GetCurrentPageNumber),
        switchMap((payload) =>
            this.groupService.getCurrentPageNumber$(payload.pageIndex).pipe(
                map((currentPageNumber: number) => {
                    return GetCurrentPageNumberSuccess({ currentPageNumber: currentPageNumber });
                }),
                catchError((error: any) => {
                    return of(GetCurrentPageNumberError({ error: error }));
                })
            )
        )
    )
);

public getLearnerIdsToAssignEffect$ = createEffect(() => 
  this.action$.pipe(
    ofType(ProcessGetLearnerIdsToAssign),
    switchMap((payload) => 
      this.groupService.getLearnerIdsToAssignCourse$(payload.groupIds).pipe(
        map((result: number[]) => {
          return ProcessGetLearnerIdsToAssignSuccess({learnerIds: result});
        }),
        catchError((error) => {
          return of(ProcessGetLearnerIdsToAssignError({error: error}));
        })
      )
    )
  )
);

  public getGroupListByOrgAndOptionalCentreIdsEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(GetGroupByOrgOptionalCentre),
      switchMap((payload) =>
        this.groupService.getGroupListByOrgAndOptionalCentreIds$(payload.orgCentreIds).pipe(
          map((result: Group[]) => {
            return GetGroupByOrgOptionalCentreSuccess({ result: result });
          }),
          catchError((error: HttpErrorResponse) => {
            return of(GetGroupByOrgOptionalCentreError({ error: error }));
          })
        )
      )
    )
  );

  public getGroupListByOrgAndOptionalCentreIdsTutorFlowEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(GetGroupByOrgOptionalCentreTutorFlow),
      switchMap((payload) =>
        this.groupService.getGroupListByOrgAndOptionalCentreIdsTutorFlow$(payload.orgCentreIds).pipe(
          map((result: Group[]) => {
            return GetGroupByOrgOptionalCentreTutorFlowSuccess({ result: result });
          }),
          catchError((error: HttpErrorResponse) => {
            return of(GetGroupByOrgOptionalCentreTutorFlowError({ error: error }));
          })
        )
      )
    )
  );
}
